import { AGClientSocket } from "socketcluster-client";
import { ObservableStore } from "../obvservable";
import { AsyncRequestsList } from "../../app/AsyncRequestsList";
import { act, auth, orgUser, userPlan } from "../../../../../../common";
import { type } from "../../../../../../global/dist/entities/org-user";
import { BasicErrorStore } from "../../app/BasicErrorStore";
import { computed } from "mobx";

// IApiData
export interface IApiData {
	state: AGClientSocket.States;
	authState: AGClientSocket.AuthStates;
}

/**
 * AbstractApi
 */
export abstract class AbstractApi<D extends IApiData = IApiData> extends ObservableStore<D> {
	// Async requests
	public readonly asyncRequests = new AsyncRequestsList();

	// Errors
	public readonly errors = new BasicErrorStore();

	// Socket
	protected __socket: AGClientSocket;

	// Identity
	protected __identity: orgUser.membership.Identity;

	/**
	 * Class constructor
	 * @param data
	 * @param toaster
	 */
	public constructor(data: D) {
		super(data);
	}

	/**
	 * Socket getter
	 */
	public get socket() {
		return this.__socket;
	}

	/**
	 * Shorthand for socket.id
	 */
	public get id() {
		return this.__socket.id;
	}

	/**
	 * Auth token for authenticated sockets
	 */
	public get authToken() {
		return this.__socket.authToken as auth.IAccessTokenPayload;
	}

	/**
	 * State
	 */
	public get state() {
		return this.data.state;
	}

	/**
	 * Auth state
	 */
	public get authState() {
		return this.data.authState;
	}

	/**
	 * Identity getter for authenticated users
	 */
	public get identity() {
		return this.__identity;
	}

	/**
	 * Connected state
	 */
	@computed public get connected() {
		return this.state === this.socket.OPEN;
	}

	/**
	 * Auth state
	 */
	@computed public get authenticated() {
		return this.authState === this.socket.AUTHENTICATED;
	}

	/**
	 * HAs facilitator role
	 */
	public get isFacilitator() {
		return this.identity?.roles.facilitator;
	}

	/**
	 * Has designer role
	 */
	public get isDesigner() {
		return this.identity?.roles.designer;
	}

	/**
	 * Is organization admin
	 */
	public get isAdmin() {
		return this.identity?.roles.admin;
	}

	/**
	 * Is organization owner
	 */
	public get isOwner() {
		return this.identity?.roles.owner;
	}

	/**
	 * Is superuser
	 */
	public get isSuperuser() {
		return this.identity?.roles.superuser;
	}

	/**
	 * Is org member
	 */
	public get isOrgMember() {
		return this.identity?.org.type === type.member;
	}

	/**
	 * Is org guest
	 */
	public get isOrgGuest() {
		return this.identity?.org.type === type.guest;
	}

	// Default error handler
	protected __defaultErrorHandler = (err: Error) => {
		this.errors.setError(err);
	};

	/**
	 * Invoke
	 * @param event
	 * @param req
	 */
	public invokeRaw<R, D>(event: string, req: R, errHandler?: (err: Error) => void): Promise<D> {
		const p = this.socket.invoke(event, req) as Promise<D>;

		errHandler || (errHandler = this.__defaultErrorHandler);

		p.catch(errHandler);

		return p as any;
	}

	/**
	 * Transmit
	 * @param event
	 * @param req
	 */
	public transmitRaw<T>(event: string, req: T, errHandler?: (err: Error) => void): Promise<void> {
		const p = this.socket.transmit(event, req);

		console.log("event", errHandler);

		errHandler || (errHandler = (err: Error) => this.errors.setError(err));
		p.catch(errHandler);

		return p;
	}

	/**
	 * Global custom action
	 * @param action
	 * @param payload
	 * @returns
	 */
	public globalCustomAction<P, R = any>(action: string, payload: P, addToAsyncRequestsList = false, errHandler?: (err: Error) => void): Promise<R> {
		const p = this.invokeRaw<act.api.custom.IPacket, R>(
			"globalCustomAction",
			{
				type: "globalCustomAction",
				action,
				origin: this.socket.id,
				payload,
			},
			errHandler
		);

		/*
		p.catch((err) => {
			console.warn("GlobalCustomActione error", action, { payload, err });
		});
        */

		if (addToAsyncRequestsList) this.asyncRequests.add(p);

		return p;
	}

	/**
	 * Custom action with wrning
	 * @param action
	 * @param payload
	 */
	public async globalCustomActionWithPlanWarning<P, R = any>(
		action: string,
		payload: P,
		addToAsyncRequestsList = false,
		errHandler?: (err: Error) => void
	): Promise<R | void> {
		try {
			const res = await this.globalCustomAction(action, payload, addToAsyncRequestsList, errHandler);

			if ((res as userPlan.ILimitWarning).warning) {
				this.errors.mset({ planWarning: res });
				return void 0;
			}

			return res;
		} catch (err) {
			return void 0;
		}
	}
}
